<template>
  <div class="data-mode">
    <!-- Mobile Warning Modal -->
    <div v-if="showMobileWarning" class="mobile-warning-overlay">
      <div class="mobile-warning-modal">
        <h2>Desktop Only</h2>
        <p>This visualization is optimized for desktop browsers.</p>
        <p>For the best experience, please visit on a larger screen.</p>
        <button @click="dismissMobileWarning">OK</button>
      </div>
    </div>
    <!-- Header Bar (50px tall) - hidden during loading -->
    <div class="header-bar" ref="headerBarRef" :class="{ 'loading-hidden': !isFullyLoaded, 'mobile-mode': isMobileMode }">
      <div class="logo-section">
        <button class="logo" @click="openAboutModal" title="About MEGA.ARMY">
          <LogoIcon class="logo-svg" />
        </button>
        <!-- Recruit Button (next to logo) -->
        <button
          v-show="!isMobileMode"
          class="recruit-btn"
          @click="openRecruitmentModal"
          title="Join the MEGA ARMY"
        >
          ENLIST
        </button>
      </div>

      <!-- Search Bar -->
      <div v-show="!isMobileMode" class="search-container" ref="searchContainerRef">
        <MagnifyingGlassIcon class="search-icon" />
        <input
          type="text"
          class="search-input"
          v-model="searchQuery"
          placeholder="Search dapp, address, or block..."
          @keydown.enter="handleSearch"
          @input="onSearchInput"
          @focus="showSearchResults = filteredProjects.length > 0"
          @blur="hideSearchResultsDelayed"
        />
        <button v-if="searchQuery" class="search-clear" @click="clearSearch">
          <XMarkIcon class="clear-icon" />
        </button>
        <!-- Search Results Dropdown -->
        <div v-if="showSearchResults && filteredProjects.length > 0" class="search-results">
          <!-- Category Filter -->
          <div class="search-category-filter">
            <CategoryFilter v-model="selectedCategory" />
          </div>
          <div
            v-for="project in filteredProjects"
            :key="project.name"
            class="search-result-item"
            @mousedown.prevent="selectProject(project)"
          >
            <div class="result-icon-wrapper">
              <img v-if="project.img && project.img.length > 0" :src="project.img" :alt="project.name" class="result-icon" @error="($event.target as HTMLImageElement).style.display = 'none'" />
              <span v-if="isProjectLive(project)" class="live-indicator" title="Active"></span>
            </div>
            <div class="result-info">
              <div class="result-name-row">
                <span class="result-name">{{ project.name }}</span>
                <span v-if="isMegaMafia(project.name || '')" class="megamafia-badge">MegaMafia</span>
                <span v-if="isProjectLive(project)" class="live-badge">Live</span>
              </div>
              <div class="result-meta">
                <span v-if="project.category" class="category-pill">{{ project.category }}</span>
              </div>
              <div v-if="project.description" class="result-description">{{ project.description }}</div>
            </div>
          </div>
        </div>
      </div>

      <div v-show="!isMobileMode" class="controls">
        <!-- Stats Button -->
        <button
          class="header-btn icon-btn"
          @click="openStatsModal"
          data-tooltip="Stats"
        >
          <ChartBarIconSolid class="btn-icon" />
        </button>
        <!-- Settings Button -->
        <button
          class="header-btn icon-btn"
          @click="openSettingsModal"
          data-tooltip="Settings"
        >
          <Cog6ToothIconSolid class="btn-icon" />
        </button>
        <!-- Pause/Resume Button -->
        <button
          class="header-btn icon-btn"
          :class="{ pause: !state.isPaused.value, resume: state.isPaused.value }"
          @click="togglePause"
          :data-tooltip="state.isPaused.value ? 'Resume (Space)' : 'Pause (Space)'"
        >
          <PlayIconSolid v-if="state.isPaused.value" class="btn-icon" />
          <PauseIconSolid v-else class="btn-icon" />
        </button>
      </div>

      <!-- Mobile Menu Button -->
      <button v-show="isMobileMode" class="mobile-menu-btn" @click="toggleMobileMenu" title="Menu">
        <Bars3Icon class="menu-icon" />
      </button>
    </div>

    <!-- Mobile Menu Dropdown -->
    <div v-if="isMobileMenuOpen" class="mobile-menu-dropdown" @click.self="closeMobileMenu">
      <!-- Mobile Search -->
      <div class="mobile-search-container">
        <MagnifyingGlassIcon class="search-icon" />
        <input
          type="text"
          class="mobile-search-input"
          v-model="searchQuery"
          placeholder="Search dapp, address, or block..."
          @keydown.enter="handleMobileSearch"
        />
        <button v-if="searchQuery" class="search-clear" @click="clearSearch">
          <XMarkIcon class="clear-icon" />
        </button>
      </div>
      <!-- Mobile Menu Items -->
      <button class="mobile-menu-item enlist" @click="handleMobileEnlist">
        ENLIST
      </button>
      <button class="mobile-menu-item" @click="handleMobileStats">
        STATS
      </button>
      <button class="mobile-menu-item" @click="handleMobileSettings">
        SETTINGS
      </button>
      <button class="mobile-menu-item" :class="{ resume: state.isPaused.value }" @click="handleMobilePause">
        <PlayIconSolid v-if="state.isPaused.value" class="btn-icon" />
        <PauseIconSolid v-else class="btn-icon" />
        {{ state.isPaused.value ? 'RESUME' : 'PAUSE' }}
      </button>
    </div>

    <!-- Pixi.js Canvas (full width, 50px shorter) - fades in when loaded -->
    <div class="pixi-container" :class="{ 'loaded': isFullyLoaded }" ref="pixiContainer"></div>

    <!-- Connection/Syncing/Paused Status Indicator - hidden during loading -->
    <div v-if="state.isSyncing.value && isFullyLoaded" class="status-indicator syncing">
      <span class="status-spinner"></span>
      <span class="status-text">Syncing. Data may be missing.{{ syncCountdown > 0 ? ` (${syncCountdown}s)` : '' }}</span>
    </div>
    <div v-else-if="state.isPaused.value && isFullyLoaded" class="status-indicator paused">
      <span class="status-dot"></span>
      <span class="status-text">Paused</span>
    </div>
    <div v-else-if="state.connectionState.value === 'reconnecting' && isFullyLoaded" class="status-indicator reconnecting">
      <span class="status-spinner"></span>
      <span class="status-text">Reconnecting{{ state.reconnectAttempt.value > 0 ? ` (attempt ${state.reconnectAttempt.value})` : '' }}...</span>
      <button class="retry-btn" @click="forceReconnect">Retry Now</button>
    </div>
    <div v-else-if="isFullyLoaded" class="status-indicator" :class="state.wsConnected.value ? 'connected' : 'disconnected'">
      <span class="status-dot"></span>
      <span class="status-text">{{ state.wsConnected.value ? 'Connected' : 'Disconnected' }}</span>
      <button v-if="!state.wsConnected.value" class="retry-btn" @click="forceReconnect">Reconnect</button>
    </div>

    <!-- Miniblock Modal -->
    <MiniblockModal ref="miniblockModal" />

    <!-- Project Modals (multiple instances) -->
    <ProjectModal
      v-for="modal in projectModals"
      :key="modal.id"
      :ref="(el) => setModalRef(modal.id, el)"
      :modal-id="modal.id"
      :z-index-base="1000"
      @close="handleModalClose"
      @focus="handleModalFocus"
      @minimize="handleModalMinimize"
      @unsave="handleCircleUnsave"
      @delete="handleCircleDelete"
      @add-circle="handleAddCircle"
      @save-position="handleSavePosition"
    />

    <!-- Stats Modal -->
    <StatsModal
      ref="statsModal"
      :state="state"
      :z-index-base="1000"
      @close="handleStatsModalClose"
      @focus="handleStatsModalFocus"
    />

    <!-- Recruitment Modal -->
    <RecruitmentModal
      ref="recruitmentModal"
      :z-index-base="1000"
      @close="handleRecruitmentModalClose"
      @focus="handleRecruitmentModalFocus"
    />

    <!-- Settings Modal -->
    <SettingsModal
      ref="settingsModal"
      :z-index-base="1000"
      :chart-mode="state.chartMode.value"
      :tx-multiplier="state.txMultiplier.value"
      :is-dev="isDev"
      @close="handleSettingsModalClose"
      @focus="handleSettingsModalFocus"
      @update:chart-mode="handleChartModeChange"
      @update:tx-multiplier="handleTxMultiplierChange"
      @restore-circle="handleRestoreCircle"
    />

    <!-- Sequencer Modal -->
    <SequencerModal
      ref="sequencerModal"
      :z-index-base="1000"
      @close="handleSequencerModalClose"
      @focus="handleSequencerModalFocus"
    />

    <!-- About Modal -->
    <AboutModal
      ref="aboutModal"
      :z-index-base="1000"
      @close="handleAboutModalClose"
      @focus="handleAboutModalFocus"
      @start-tutorial="handleStartTutorial"
    />

    <!-- Tutorial Overlay -->
    <TutorialOverlay />

    <!-- Minimized Modals Tab Bar -->
    <div v-if="allMinimizedModals.length > 0" class="minimized-tabs-bar">
      <div
        v-for="modal in allMinimizedModals"
        :key="modal.id"
        class="minimized-tab"
        @click="handleRestoreAnyModal(modal.id, modal.type)"
      >
        <span class="tab-title">{{ modal.title }}</span>
        <button class="tab-close" @click.stop="handleCloseAnyModal(modal.id, modal.type)" title="Close">
          <XMarkIcon class="tab-close-icon" />
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, watch, ref, computed, reactive } from 'vue';
import { MagnifyingGlassIcon, XMarkIcon, PlayIconSolid, PauseIconSolid, LogoIcon, Bars3Icon, ChartBarIconSolid, Cog6ToothIconSolid } from '@/components/icons';
import { useVisualization } from '@/composables/useVisualization';
import { useWebSocket } from '@/composables/useWebSocket';
import { useBlockchainData } from '@/composables/useBlockchainData';
import { useWallet } from '@/composables/useWallet';
import { BlockchainState } from '@/stores/blockchainState';
import { userSettings } from '@/stores/userSettings';
import { PROJECTS, ADDRESS_TO_PROJECT, isMegaMafia, type Category } from '@/data/projects';
import CategoryFilter from '@/components/CategoryFilter.vue';
import MiniblockModal from '@/components/MiniblockModal.vue';
import ProjectModal from '@/components/ProjectModal.vue';
import StatsModal from '@/components/StatsModal.vue';
import RecruitmentModal from '@/components/RecruitmentModal.vue';
import SettingsModal from '@/components/SettingsModal.vue';
import SequencerModal from '@/components/SequencerModal.vue';
import AboutModal from '@/components/AboutModal.vue';
import TutorialOverlay from '@/components/TutorialOverlay.vue';
import { useTutorial } from '@/stores/tutorialState';
import type { MiniblockData } from '@/rendering/BarGraphRenderer';
import type { Project } from '@/data/projects';
import type { CircleMetrics } from '@/rendering/CircleRenderer';

// WebSocket URL from config (Fly.io handles geo-routing automatically)
import { WS_URL } from '@/config/visualization.config';

// Dev mode detection (for performance testing controls)
const isDev = import.meta.env.DEV;

// Tutorial system
const tutorial = useTutorial();

// Initialize wallet (just need isConnected for button state)
const { isConnected } = useWallet();

// Search functionality
const searchQuery = ref('');
const showSearchResults = ref(false);
const searchContainerRef = ref<HTMLElement | null>(null);
const selectedCategory = ref<Category>('All');
const EXPLORER_BASE_URL = 'https://mega.etherscan.io';

// Filter projects based on search query and category
const filteredProjects = computed(() => {
  const query = searchQuery.value.trim().toLowerCase();

  // Start with all projects or just query-matched projects
  let results = PROJECTS;

  // Apply search query filter
  if (query && query.length >= 1) {
    results = results.filter(project => {
      const name = project.name?.toLowerCase() || '';
      const description = project.description?.toLowerCase() || '';
      const category = project.category?.toLowerCase() || '';
      return name.includes(query) || description.includes(query) || category.includes(query);
    });
  }

  // Apply category filter
  if (selectedCategory.value !== 'All') {
    results = results.filter(project => {
      const cat = project.category?.toLowerCase() || '';
      const selected = selectedCategory.value.toLowerCase();
      // Match category (handle variations like "DeFi" matching "defi")
      return cat === selected || cat.includes(selected) || selected.includes(cat);
    });
  }

  // Only show results if there's a query OR a non-All category selected
  if (!query && selectedCategory.value === 'All') return [];

  return results.slice(0, 12); // Increased to 12 results
});

// isProjectLive is defined after renderers initialization below

const onSearchInput = () => {
  showSearchResults.value = filteredProjects.value.length > 0 || selectedCategory.value !== 'All';
};

// Watch for category changes to show results
watch(selectedCategory, () => {
  showSearchResults.value = filteredProjects.value.length > 0;
});

const hideSearchResultsDelayed = () => {
  // Delay to allow click on result
  setTimeout(() => {
    showSearchResults.value = false;
  }, 200);
};

const selectProject = (project: Project) => {
  showSearchResults.value = false;
  searchQuery.value = '';

  // Find address for this project (reverse lookup)
  const address = Object.entries(ADDRESS_TO_PROJECT).find(
    ([_, name]) => name.toLowerCase() === project.name?.toLowerCase()
  )?.[0];

  // Always open the modal for the project
  openProjectModalForProject(project, address);
};

// Open modal for a project (with or without tracked address)
const openProjectModalForProject = (project: Project, address?: string) => {
  const modalId = `modal_${nextModalId++}`;
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  // Position in center
  const modalX = Math.max(20, (viewportWidth - 480) / 2);
  const modalY = Math.max(70, (viewportHeight - 500) / 2);

  projectModals.value.push({
    id: modalId,
    projectId: project.name || 'unknown',
    zIndex: 1000 + projectModals.value.length,
    isMinimized: false,
    title: project.name || 'Unknown'
  });

  // Wait for the modal to be created, then open it
  setTimeout(() => {
    const modalRef = modalRefs.get(modalId);
    if (modalRef) {
      if (address && renderers.circle) {
        // Get circle info if it exists
        const circleExists = renderers.circle.hasCircleForAddress(address);
        if (circleExists) {
          // Circle exists - open with live metrics and hasCircle=true
          const getMetrics = () => renderers.circle?.getMetricsForAddress(address) || null;
          const getHasCircle = () => renderers.circle?.hasCircleForAddress(address) || false;
          const getSavedStatus = () => renderers.circle?.isCircleSaved(address) || false;
          const initialMetrics = getMetrics() || { tps: 0, gps: 0, lastMinuteCount: 0, totalTxCount: 0, totalGasUsed: 0 };
          modalRef.open(project, address, initialMetrics, modalX, modalY, getMetrics, true, getHasCircle, getSavedStatus());
        } else {
          // Circle doesn't exist yet - open with empty metrics, hasCircle=false
          const emptyMetrics: CircleMetrics = {
            tps: 0,
            gps: 0,
            lastMinuteCount: 0,
            totalTxCount: 0,
            totalGasUsed: 0
          };
          const getHasCircle = () => renderers.circle?.hasCircleForAddress(address) || false;
          modalRef.open(project, address, emptyMetrics, modalX, modalY, undefined, false, getHasCircle, false);
        }
      } else {
        // No contract address - show "not tracked yet"
        const emptyMetrics: CircleMetrics = {
          tps: 0,
          gps: 0,
          lastMinuteCount: 0,
          totalTxCount: 0,
          totalGasUsed: 0
        };
        modalRef.open(project, 'not-tracked', emptyMetrics, modalX, modalY);
      }
    }
  }, 50);
};

const handleSearch = () => {
  const query = searchQuery.value.trim();
  if (!query) return;

  let url = '';

  // Detect query type and build URL
  if (/^0x[a-fA-F0-9]{64}$/.test(query)) {
    // Transaction hash (66 chars with 0x)
    url = `${EXPLORER_BASE_URL}/tx/${query}`;
  } else if (/^0x[a-fA-F0-9]{40}$/.test(query)) {
    // Address (42 chars with 0x)
    url = `${EXPLORER_BASE_URL}/address/${query}`;
  } else if (/^\d+$/.test(query)) {
    // Block number
    url = `${EXPLORER_BASE_URL}/block/${query}`;
  } else {
    // Default: try as search
    url = `${EXPLORER_BASE_URL}/search?q=${encodeURIComponent(query)}`;
  }

  // Open in new tab
  window.open(url, '_blank');
};

const clearSearch = () => {
  searchQuery.value = '';
  showSearchResults.value = false;
};

// Mobile warning modal
const showMobileWarning = ref(false);

const dismissMobileWarning = () => {
  showMobileWarning.value = false;
};

// Mobile menu
const isMobileMenuOpen = ref(false);
const isMobileMode = ref(false);
const headerBarRef = ref<HTMLElement | null>(null);

// Store measured widths of desktop elements (measured once when visible)
let desktopContentWidth = 0;

// Measure header content widths (called once on mount when all elements are visible)
const measureDesktopContent = () => {
  if (!headerBarRef.value) return;

  const logoSection = headerBarRef.value.querySelector('.logo-section') as HTMLElement;
  const searchContainer = headerBarRef.value.querySelector('.search-container') as HTMLElement;
  const controls = headerBarRef.value.querySelector('.controls') as HTMLElement;

  if (!logoSection) return;

  // Calculate total content width needed
  // Logo section + search (min 280px + 40px margins) + controls + header padding
  const padding = 40; // 20px padding on each side
  const searchMargins = 40; // 20px on each side

  desktopContentWidth = logoSection.offsetWidth + padding;

  if (searchContainer) {
    // Use min-width of search (280px) for consistent threshold
    desktopContentWidth += 280 + searchMargins;
  }

  if (controls) {
    desktopContentWidth += controls.offsetWidth;
  }

  // Now check if it fits
  checkMobileMode();
};

const checkMobileMode = () => {
  if (!headerBarRef.value || desktopContentWidth === 0) return;

  const headerWidth = headerBarRef.value.offsetWidth;

  // Switch to mobile mode if content doesn't fit
  const needsMobileMode = desktopContentWidth > headerWidth;

  if (needsMobileMode !== isMobileMode.value) {
    isMobileMode.value = needsMobileMode;
    // Close mobile menu when switching to desktop mode
    if (!needsMobileMode) {
      isMobileMenuOpen.value = false;
    }
  }
};

// Set up ResizeObserver for dynamic detection
let resizeObserver: ResizeObserver | null = null;

const toggleMobileMenu = () => {
  isMobileMenuOpen.value = !isMobileMenuOpen.value;
};

const closeMobileMenu = () => {
  isMobileMenuOpen.value = false;
};

const handleMobileSearch = () => {
  handleSearch();
  closeMobileMenu();
};

const handleMobileEnlist = () => {
  openRecruitmentModal();
  closeMobileMenu();
};

const handleMobileStats = () => {
  openStatsModal();
  closeMobileMenu();
};

const handleMobileSettings = () => {
  openSettingsModal();
  closeMobileMenu();
};

const handleMobilePause = () => {
  togglePause();
  closeMobileMenu();
};

// Create blockchain state
const state = new BlockchainState();

// Initialize chart mode from saved settings immediately (before watchers run)
state.chartMode.value = userSettings.chartMode;

// Loading state tracking
const loadingState = reactive({
  renderersReady: false,
  wsConnected: false,
  initialStateReceived: false
});

const isFullyLoaded = computed(() =>
  loadingState.renderersReady &&
  loadingState.wsConnected &&
  loadingState.initialStateReceived
);

// Initialize visualization
const visualization = useVisualization(state);
const { pixiContainer, renderers, setOnRenderersReady, setTutorialHighlights, resetTutorialHighlights } = visualization;

// Initialize blockchain data handling
const blockchainData = useBlockchainData(state, renderers);
const { handleMiniblock, handleMiniblockUpdate, handleBlockUpdate, handleInitialState, initializeTimers, getNextMiniblock, getBlockVerification, pause, resume, syncCountdown, resetStaleData } = blockchainData;

// Check if a project has an active circle (live) - must be after renderers init
const isProjectLive = (project: Project): boolean => {
  if (!renderers.circle) return false;
  const address = Object.entries(ADDRESS_TO_PROJECT).find(
    ([_, name]) => name.toLowerCase() === project.name?.toLowerCase()
  )?.[0];
  if (!address) return false;
  return renderers.circle.hasCircleForAddress(address);
};

// Modal references
const miniblockModal = ref<InstanceType<typeof MiniblockModal> | null>(null);
const statsModal = ref<InstanceType<typeof StatsModal> | null>(null);
const recruitmentModal = ref<InstanceType<typeof RecruitmentModal> | null>(null);
const settingsModal = ref<InstanceType<typeof SettingsModal> | null>(null);
const sequencerModal = ref<InstanceType<typeof SequencerModal> | null>(null);
const aboutModal = ref<InstanceType<typeof AboutModal> | null>(null);

// Project modal manager
interface ModalInstance {
  id: string;
  projectId: string;
  zIndex: number;
  isMinimized: boolean;
  title: string;
}

const projectModals = ref<ModalInstance[]>([]);
const modalRefs = new Map<string, InstanceType<typeof ProjectModal>>();
let nextModalId = 0;
let nextZIndex = 1000;

// Set modal ref (called by v-for template ref)
const setModalRef = (modalId: string, el: any) => {
  if (el) {
    modalRefs.set(modalId, el);
  } else {
    modalRefs.delete(modalId);
  }
};

// Handle modal close
const handleModalClose = (modalId: string) => {
  const index = projectModals.value.findIndex(m => m.id === modalId);
  if (index !== -1) {
    projectModals.value.splice(index, 1);
  }
  modalRefs.delete(modalId);
  // console.log('📋 Modal closed, remaining:', projectModals.value.length);
};

// Handle modal focus (bring to front) - flash is optional, only used when external element triggers focus
const handleModalFocus = (modalId: string, shouldFlash: boolean = false) => {
  const modal = projectModals.value.find(m => m.id === modalId);
  if (!modal) return;

  // Bring focused modal to front using next z-index
  modal.zIndex = ++nextZIndex;
  const ref = modalRefs.get(modalId);
  if (ref) {
    ref.setZIndex(modal.zIndex);
    if (shouldFlash) {
      ref.flashHeader(); // Only flash when triggered externally (e.g., clicking circle)
    }
  }

  // console.log('📋 Modal focused:', modalId);
};

// Handle modal minimize
const handleModalMinimize = (modalId: string, title: string) => {
  const modal = projectModals.value.find(m => m.id === modalId);
  if (modal) {
    modal.isMinimized = true;
    modal.title = title;
    // Persist to settings
    userSettings.addMinimizedWindow({
      id: modalId,
      type: 'project',
      title,
      projectId: modal.projectId
    });
    // console.log('📋 Modal minimized:', title);
  }
};

// Handle modal restore from minimized state
const handleModalRestore = (modalId: string) => {
  const modal = projectModals.value.find(m => m.id === modalId);
  if (!modal) return;

  modal.isMinimized = false;
  modal.zIndex = ++nextZIndex;

  const ref = modalRefs.get(modalId);
  if (ref) {
    ref.restore();
    ref.setZIndex(modal.zIndex);
  }

  // console.log('📋 Modal restored:', modal.title);
};

// Get minimized modals for tab bar (only project modals can be minimized now)
const minimizedModals = computed(() => projectModals.value.filter(m => m.isMinimized));

// All minimized modals (just project modals now)
const allMinimizedModals = computed(() =>
  minimizedModals.value.map(m => ({ id: m.id, title: m.title, type: 'project' as const }))
);

// Open (non-minimized) modals - used for circle highlighting
const openModals = computed(() => projectModals.value.filter(m => !m.isMinimized));

// Watch for open modal changes and update circle highlights
watch(openModals, (modals) => {
  if (renderers.circle) {
    const addresses = modals.map(m => m.projectId);
    renderers.circle.setHighlightedCircles(addresses);
  }
}, { deep: true });

// Handle restore for minimized project modals
const handleRestoreAnyModal = (modalId: string, type: string) => {
  if (type !== 'project') return;

  const modal = projectModals.value.find(m => m.id === modalId);
  if (!modal) return;

  // Check if this is a placeholder modal loaded from persistence (hasn't been opened yet)
  const isPlaceholder = modalId.startsWith('saved-');

  if (isPlaceholder) {
    // This is a saved placeholder - we need to open it properly
    // Remove the placeholder from the list
    projectModals.value = projectModals.value.filter(m => m.id !== modalId);

    // Remove from saved state
    userSettings.removeMinimizedWindow('project', modal.projectId);

    // Try to find circle data and trigger a proper open
    if (renderers.circle) {
      const address = modal.projectId;
      const project = renderers.circle.getProjectForAddress(address);
      const metrics = renderers.circle.getMetricsForAddress(address);

      if (metrics) {
        // Simulate a circle click to open the modal properly
        const centerX = window.innerWidth / 2;
        const centerY = window.innerHeight / 2;
        handleCircleClick(project, address, centerX, centerY, metrics);
      } else {
        // console.warn('📋 Could not find metrics for address:', address);
      }
    }
    return;
  }

  // Normal restore for an already-opened modal
  handleModalRestore(modalId);
  userSettings.removeMinimizedWindow('project', modal.projectId);
};

// Handle close for minimized project modals
const handleCloseAnyModal = (modalId: string, type: string) => {
  if (type !== 'project') return;

  // Find the projectId before closing
  const modal = projectModals.value.find(m => m.id === modalId);
  if (modal) {
    userSettings.removeMinimizedWindow('project', modal.projectId);
  }
  handleModalClose(modalId);
};

// Handle bar click (only when paused)
const handleBarClick = (miniblock: MiniblockData) => {
  if (state.isPaused.value && miniblockModal.value) {
    miniblockModal.value.open(miniblock);
  }
};

// Handle circle click
const handleCircleClick = (project: Project | null, address: string, mouseX: number, mouseY: number, metrics: CircleMetrics) => {
  // console.warn('📋 handleCircleClick called:', project?.name || address, 'at', mouseX, mouseY, 'metrics:', metrics);

  // Use address as identifier (always unique)
  const projectIdentifier = address;

  // Check if modal for this address already exists
  const existingModal = projectModals.value.find(m => m.projectId === projectIdentifier);

  if (existingModal) {
    // If modal is minimized, restore it instead of just focusing
    if (existingModal.isMinimized) {
      handleRestoreAnyModal(existingModal.id, 'project');
      return;
    }
    // Bring existing modal to front with flash (clicked from canvas)
    handleModalFocus(existingModal.id, true);
    return;
  }

  // If this project was previously minimized (persisted from last session), remove it from saved state
  // since the user is now clicking on it to open it
  if (userSettings.isWindowMinimized('project', projectIdentifier)) {
    userSettings.removeMinimizedWindow('project', projectIdentifier);
  }

  // Create new modal
  const modalId = `modal-${nextModalId++}`;
  const newModal: ModalInstance = {
    id: modalId,
    projectId: projectIdentifier,
    zIndex: ++nextZIndex,
    isMinimized: false,
    title: project?.name || (address ? `${address.substring(0, 8)}...` : 'Unknown')
  };

  projectModals.value.push(newModal);

  // Wait for next tick for the modal to be created
  setTimeout(() => {
    const modalRef = modalRefs.get(modalId);
    if (modalRef) {
      // Create a getter function for live metrics updates
      const getMetrics = () => {
        if (renderers.circle) {
          return renderers.circle.getMetricsForAddress(address);
        }
        return null;
      };
      // Create a getter function for saved status updates
      const getSavedStatus = () => {
        if (renderers.circle) {
          return renderers.circle.isCircleSaved(address);
        }
        return false;
      };
      // Get initial saved status
      const savedStatus = renderers.circle?.isCircleSaved(address) ?? false;
      // Pass project (may be null), address, initial metrics, metrics getter, saved status, and saved status getter to modal
      modalRef.open(project, address, mouseX, mouseY, metrics, getMetrics, savedStatus, getSavedStatus);
      modalRef.setZIndex(newModal.zIndex);
    }
  }, 0);
};

// Handle circle unsave from modal
const handleCircleUnsave = (address: string) => {
  if (renderers.circle) {
    renderers.circle.unsaveCircle(address);
    // console.log('📋 Circle unsaved from modal:', address);
  }
};

// Handle circle delete from modal
const handleCircleDelete = (address: string, projectName: string | undefined) => {
  if (renderers.circle) {
    renderers.circle.deleteCircle(address);
    // console.log('📋 Circle deleted from modal:', projectName || address);
  }
};

// Handle add circle from modal
const handleAddCircle = (address: string) => {
  if (renderers.circle) {
    renderers.circle.getOrCreateCircleForAddress(address, 0);
    renderers.circle.saveCirclePosition(address);  // Auto-save to prevent removal by inactivity timeout
    // console.log('📋 Circle added and saved from modal:', address);
  }
};

// Handle save position from modal
const handleSavePosition = (address: string) => {
  if (renderers.circle) {
    renderers.circle.saveCirclePosition(address);
    // console.log('📋 Circle position saved from modal:', address);
  }
};

// Handle "More Stats" button click from StatsRenderer
const handleMoreStatsClick = (mouseX: number, mouseY: number) => {
  if (statsModal.value) {
    statsModal.value.open(mouseX, mouseY);
  }
};

// Handle stats modal close
const handleStatsModalClose = () => {
  // console.log('📊 Stats modal closed');
};

// Handle stats modal focus
const handleStatsModalFocus = () => {
  if (statsModal.value) {
    statsModal.value.setZIndex(++nextZIndex);
  }
};

// Open recruitment modal
const openRecruitmentModal = () => {
  if (recruitmentModal.value) {
    recruitmentModal.value.open();
    recruitmentModal.value.setZIndex(++nextZIndex);
  }
};

// Handle recruitment modal close
const handleRecruitmentModalClose = () => {
  // console.log('⚔️ Recruitment modal closed');
};

// Handle recruitment modal focus
const handleRecruitmentModalFocus = () => {
  if (recruitmentModal.value) {
    recruitmentModal.value.setZIndex(++nextZIndex);
  }
};

// Open about modal (click on logo)
const openAboutModal = () => {
  if (aboutModal.value) {
    aboutModal.value.open();
    aboutModal.value.setZIndex(++nextZIndex);
  }
};

// Handle about modal close
const handleAboutModalClose = () => {
  // console.log('ℹ️ About modal closed');
};

// Handle about modal focus
const handleAboutModalFocus = () => {
  if (aboutModal.value) {
    aboutModal.value.setZIndex(++nextZIndex);
  }
};

// Handle start tutorial from about modal
const handleStartTutorial = () => {
  // Close the about modal first
  if (aboutModal.value) {
    aboutModal.value.close();
  }
  // Start the tutorial
  tutorial.startTutorial();
};

// Open stats modal from header button
const openStatsModal = () => {
  if (statsModal.value) {
    // Open centered on screen
    const centerX = window.innerWidth / 2;
    const centerY = window.innerHeight / 2;
    statsModal.value.open(centerX, centerY);
    statsModal.value.setZIndex(++nextZIndex);
  }
};

// Open settings modal
const openSettingsModal = () => {
  if (settingsModal.value) {
    settingsModal.value.open();
    settingsModal.value.setZIndex(++nextZIndex);
  }
};

// Handle settings modal close
const handleSettingsModalClose = () => {
  // console.log('⚙️ Settings modal closed');
};

// Handle settings modal focus
const handleSettingsModalFocus = () => {
  if (settingsModal.value) {
    settingsModal.value.setZIndex(++nextZIndex);
  }
};

// Handle sequencer modal close
const handleSequencerModalClose = () => {
  // console.log('Sequencer modal closed');
};

// Handle sequencer modal focus
const handleSequencerModalFocus = () => {
  if (sequencerModal.value) {
    sequencerModal.value.setZIndex(++nextZIndex);
  }
};

// Handle sequencer click
const handleSequencerClick = (mouseX: number, mouseY: number) => {
  if (sequencerModal.value) {
    sequencerModal.value.open(mouseX, mouseY);
    sequencerModal.value.setZIndex(++nextZIndex);
  }
};

// Handle restore circle from settings
const handleRestoreCircle = (address: string) => {
  if (renderers.circle) {
    renderers.circle.restoreDeletedCircle(address);
    // console.log('⚙️ Circle restored from settings:', address);
  }
};

// Handle chart mode change from settings
const handleChartModeChange = (mode: 'gas' | 'txCount') => {
  state.chartMode.value = mode;
};

// Handle TX multiplier change from settings
const handleTxMultiplierChange = (value: number) => {
  state.txMultiplier.value = value;
};

// Connect to WebSocket (listening to initial state and miniblocks)
// Fly.io handles geo-routing automatically with single URL
const { wsClient } = useWebSocket(WS_URL, {
  onInitialState: (data) => {
    handleInitialState(data);
    loadingState.initialStateReceived = true;
  },
  onMiniblock: handleMiniblock, // Legacy format (backward compatibility)
  onMiniblockUpdate: handleMiniblockUpdate, // Lightweight miniblock updates - stats calculated locally
  onBlockUpdate: handleBlockUpdate, // Minimal block updates for miniblock verification
  onConnected: () => {
    state.wsConnected.value = true;
    state.connectionState.value = 'connected';
    state.reconnectAttempt.value = 0;
    state.lastError.value = null;
    loadingState.wsConnected = true;
  },
  onDisconnected: () => {
    state.wsConnected.value = false;
    state.connectionState.value = 'disconnected';
    loadingState.wsConnected = false;
  },
  onReconnecting: (attempt: number, maxAttempts: number) => {
    state.connectionState.value = 'reconnecting';
    state.reconnectAttempt.value = attempt;
  },
  onError: (error: string, context: string) => {
    state.lastError.value = error;
    console.error(`WebSocket error (${context}):`, error);
  },
  onStaleDataReset: () => {
    // Tab was hidden for >60s - reset stale local data before receiving fresh state
    resetStaleData();
  }
});

// Pass WebSocket client to blockchainData for requesting fresh state on resume
blockchainData.setWsClient(wsClient);

// Force reconnect function (exposed for retry button)
const forceReconnect = () => {
  wsClient.forceReconnect();
};

// Connect renderers when ready
setOnRenderersReady(() => {
  if (renderers.barGraph) {
    renderers.barGraph.setMiniblockGetter(getNextMiniblock);
    renderers.barGraph.setBarClickCallback(handleBarClick);
    renderers.barGraph.setBlockVerificationCallback(getBlockVerification);
    renderers.barGraph.setPausedState(state.isPaused.value);
    renderers.barGraph.setSequencerClickCallback(handleSequencerClick);
  }

  if (renderers.circle) {
    renderers.circle.setCircleClickCallback(handleCircleClick);
    // Pass WebSocket client for subscriptions (saved circles)
    renderers.circle.setWebSocketClient(wsClient);
  }

  if (renderers.stats) {
    renderers.stats.setOnMoreStatsClick(handleMoreStatsClick);
  }

  // Mark renderers as ready for loading state
  loadingState.renderersReady = true;

  // Auto-start tutorial for first-time visitors (wait a moment for UI to settle)
  if (!tutorial.hasSeenTutorial()) {
    setTimeout(() => {
      tutorial.startTutorial();
    }, 1000);
  }
});

// Track if user manually paused (to prevent auto-resume)
let wasManuallyPaused = false;
// Track if we auto-paused (to distinguish from manual pause)
let wasAutoPaused = false;

// Function to toggle pause/resume
const togglePause = () => {
  if (state.isPaused.value) {
    resume();
    wasManuallyPaused = false;
    wasAutoPaused = false;
  } else {
    pause();
    wasManuallyPaused = true;
    wasAutoPaused = false;
  }
};

// Watch for chart mode changes and update renderers + save to settings
watch(() => state.chartMode.value, (newMode) => {
  if (renderers.barGraph) {
    renderers.barGraph.setChartMode(newMode);
  }
  if (renderers.circle) {
    renderers.circle.setChartMode(newMode);
  }
  // Persist to localStorage (setter only saves if different)
  userSettings.chartMode = newMode;
}, { flush: 'post' }); // Run after DOM updates to avoid race with onMounted

// Watch for tutorial state changes and update canvas highlights
watch([() => tutorial.isActive.value, () => tutorial.currentHighlights.value], ([isActive, highlights]) => {
  if (isActive && highlights.length > 0) {
    setTutorialHighlights(highlights);
  } else {
    resetTutorialHighlights();
  }
});

// Watch for pause state changes and update renderer
watch(() => state.isPaused.value, (isPaused) => {
  if (renderers.barGraph) {
    renderers.barGraph.setPausedState(isPaused);
  }
});

// Watch for tx multiplier changes and toggle testing mode
watch(() => state.txMultiplier.value, (newMultiplier) => {
  const isDevMode = newMultiplier > 1;

  if (renderers.circle) {
    if (isDevMode) {
      // Entering testing mode - fill screen with circles
      renderers.circle.enterTestingMode();
    } else {
      // Back to production mode - remove test circles
      renderers.circle.exitTestingMode();
    }
  }

  // Toggle dev mode for bar graph (shows verification icons)
  if (renderers.barGraph) {
    renderers.barGraph.setDevMode(isDevMode);
  }
});

// Auto-pause when window loses focus
// Note: WebSocket stays connected for stats - miniblocks buffer automatically in useBlockchainData
const handleVisibilityChange = () => {
  // console.log('👁️ Visibility changed:', document.hidden ? 'HIDDEN' : 'VISIBLE',
  //   '| isPaused:', state.isPaused.value,
  //   '| wasAutoPaused:', wasAutoPaused,
  //   '| wasManuallyPaused:', wasManuallyPaused);

  if (document.hidden) {
    // Window lost focus - auto pause if not already paused
    if (!state.isPaused.value) {
      // console.log('👁️ Auto-pausing...');
      pause();
      wasAutoPaused = true;
      wasManuallyPaused = false;
    }
    // WebSocket stays connected - miniblocks buffer in useBlockchainData
  } else {
    // Window gained focus - auto resume only if it was auto-paused
    // console.log('👁️ Checking resume: wasAutoPaused=', wasAutoPaused);
    if (wasAutoPaused) {
      // console.log('👁️ Auto-resuming...');
      resume();
      wasAutoPaused = false;
    }
  }
};

// Keyboard shortcuts
const handleKeyPress = (event: KeyboardEvent) => {
  // Ignore if user is typing in an input
  if (event.target instanceof HTMLInputElement) return;

  switch (event.key.toLowerCase()) {
    case ' ': // Spacebar - pause/resume
      event.preventDefault();
      togglePause();
      break;
    case 'g': // G - switch to gas mode
      state.chartMode.value = 'gas';
      break;
    case 't': // T - switch to tx count mode
      state.chartMode.value = 'txCount';
      break;
  }
};

// Initialize timers on mount
onMounted(() => {
  // console.log('🎬 VisualizationView mounted');

  // Check if mobile and show warning
  // Uses pointer: coarse (touch-primary) AND screen width < 1024px
  // This avoids triggering on desktop browsers resized narrow
  const isTouchDevice = window.matchMedia('(pointer: coarse)').matches;
  const isSmallScreen = window.innerWidth < 1024;
  if (isTouchDevice && isSmallScreen) {
    showMobileWarning.value = true;
  }

  initializeTimers();

  // Chart mode already loaded from userSettings during state initialization

  // Load minimized windows from user settings (only project modals can be minimized)
  const savedMinimized = userSettings.minimizedWindows;
  for (const win of savedMinimized) {
    if (win.type === 'project' && win.projectId) {
      // Create a placeholder minimized project modal that will be restored when clicked
      projectModals.value.push({
        id: `saved-${win.projectId}`,
        projectId: win.projectId,
        zIndex: 1000,
        isMinimized: true,
        title: win.title
      });
    }
  }
  if (savedMinimized.length > 0) {
    // console.log('📋 Loaded minimized windows from settings:', savedMinimized.length);
  }

  // Add visibility change listener (primary method)
  document.addEventListener('visibilitychange', handleVisibilityChange);

  // Add keyboard shortcuts
  document.addEventListener('keydown', handleKeyPress);
  // console.log('⌨️ Keyboard shortcuts enabled: Space (pause/resume), G (gas), T (tx count)');

  // Set up ResizeObserver for dynamic mobile mode detection
  if (headerBarRef.value) {
    // Initial measurement (elements are visible on first render)
    measureDesktopContent();

    resizeObserver = new ResizeObserver(() => {
      // Only re-check based on stored width, don't re-measure
      checkMobileMode();
    });
    resizeObserver.observe(headerBarRef.value);
  }
});

// Cleanup on unmount
onUnmounted(() => {
  document.removeEventListener('visibilitychange', handleVisibilityChange);
  document.removeEventListener('keydown', handleKeyPress);
  // Cleanup ResizeObserver
  if (resizeObserver) {
    resizeObserver.disconnect();
    resizeObserver = null;
  }
  // Cleanup blockchain data (intervals, maps)
  blockchainData.cleanup();
});
</script>

<style scoped>
/* Mobile Warning Modal */
.mobile-warning-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.9);
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.mobile-warning-modal {
  background: var(--bg-surface);
  border: 1px solid var(--accent-primary);
  border-radius: 8px;
  padding: 32px;
  text-align: center;
  max-width: 90%;
}

.mobile-warning-modal h2 {
  color: var(--accent-primary);
  margin-bottom: 16px;
  font-size: 24px;
}

.mobile-warning-modal p {
  color: var(--text-secondary);
  margin-bottom: 12px;
  font-size: 14px;
}

.mobile-warning-modal button {
  background: var(--accent-primary);
  color: var(--bg-primary);
  border: none;
  padding: 12px 32px;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 600;
  margin-top: 16px;
  font-size: 14px;
}

.mobile-warning-modal button:hover {
  opacity: 0.9;
}

/* Loading state - hide elements until fully loaded */
.loading-hidden {
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.4s ease-out;
}

.header-bar:not(.loading-hidden) {
  opacity: 1;
  pointer-events: auto;
}

.data-mode {
  width: 100vw;
  height: 100vh;
  background: var(--bg-primary);
  color: var(--text-primary);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

/* Header Bar - 50px tall */
.header-bar {
  height: 50px;
  width: 100%;
  background: var(--header-bg);
  border-bottom: 1px solid var(--border);
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 20px;
  flex-shrink: 0;
}

/* Logo Section */
.logo-section {
  display: flex;
  align-items: center;
  gap: 16px;
  min-width: 180px;
  flex-shrink: 0;
}

.logo {
  display: flex;
  align-items: center;
  gap: 12px;
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
  transition: opacity 0.15s;
}

.logo:hover {
  opacity: 0.8;
}

.connection-circle {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: var(--error);
  transition: background 0.3s;
}

.connection-circle.connected {
  background: var(--success);
  animation: pulse-circle 2s ease-in-out infinite;
}

@keyframes pulse-circle {
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.7;
    transform: scale(1.2);
  }
}

.logo-svg {
  height: 28px;
  width: auto;
}

/* Search Bar */
.search-container {
  display: flex;
  align-items: center;
  background: var(--hover-overlay);
  border: 1px solid var(--border);
  border-radius: 4px;
  padding: 0 12px;
  height: 34px;
  min-width: 280px;
  max-width: 400px;
  flex: 1;
  margin: 0 20px;
  transition: all 0.3s ease;
  position: relative;
}

.search-container:focus-within {
  border-color: var(--accent-primary);
  box-shadow: var(--shadow-md);
}

/* Search Results Dropdown */
.search-results {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin-top: 4px;
  background: var(--bg-surface);
  border: 1px solid var(--border);
  border-radius: 4px;
  box-shadow: var(--shadow-lg);
  max-height: 400px;
  overflow-y: auto;
  z-index: 2000;
}

.search-result-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 14px;
  cursor: pointer;
  transition: background 0.15s;
}

.search-result-item:hover {
  background: var(--hover-overlay);
}

.search-result-item:not(:last-child) {
  border-bottom: 1px solid var(--border);
}

.result-icon {
  width: 36px;
  height: 36px;
  border-radius: 6px;
  object-fit: cover;
  flex-shrink: 0;
}

.result-icon-wrapper {
  width: 36px;
  height: 36px;
  flex-shrink: 0;
}

.result-icon-placeholder {
  width: 36px;
  height: 36px;
  border-radius: 6px;
  background: var(--hover-overlay);
  flex-shrink: 0;
}

.result-info {
  flex: 1;
  min-width: 0;
  overflow: hidden;
}

.search-category-filter {
  padding: 8px 12px;
  border-bottom: 1px solid var(--border);
  background: var(--bg-surface);
}

.result-name-row {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

.result-name {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.megamafia-badge {
  display: inline-flex;
  align-items: center;
  padding: 1px 5px;
  font-size: 9px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  background: linear-gradient(135deg, #ffd700, #ff8c00);
  color: #000;
  border-radius: 3px;
  white-space: nowrap;
}

.live-badge {
  display: inline-flex;
  align-items: center;
  padding: 1px 5px;
  font-size: 9px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  background: #22c55e;
  color: #fff;
  border-radius: 3px;
  white-space: nowrap;
}

.live-indicator {
  position: absolute;
  bottom: 2px;
  right: 2px;
  width: 8px;
  height: 8px;
  background: #22c55e;
  border-radius: 50%;
  border: 2px solid var(--bg-surface);
}

.result-icon-wrapper {
  position: relative;
  flex-shrink: 0;
}

.result-meta {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 2px;
}

.category-pill {
  display: inline-flex;
  align-items: center;
  padding: 1px 6px;
  font-size: 10px;
  font-weight: 500;
  background: var(--hover-overlay);
  color: var(--text-secondary);
  border-radius: 3px;
  border: 1px solid var(--border);
}

.result-description {
  font-size: 12px;
  color: var(--text-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-top: 3px;
  line-height: 1.4;
}

.search-icon {
  width: 16px;
  height: 16px;
  margin-right: 8px;
  color: var(--text-muted);
  flex-shrink: 0;
}

.search-input {
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  color: var(--text-primary);
  font-family: Inter, Arial, sans-serif;
  font-size: 12px;
  letter-spacing: 0.5px;
}

.search-input::placeholder {
  color: var(--text-muted);
  font-style: italic;
}

.search-clear {
  background: none;
  border: none;
  color: var(--text-muted);
  cursor: pointer;
  padding: 0 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s;
}

.search-clear:hover {
  color: var(--error);
}

.clear-icon {
  width: 14px;
  height: 14px;
}

/* Button icons */
.btn-icon {
  width: 18px;
  height: 18px;
  flex-shrink: 0;
}

/* Icon-only buttons */
.header-btn.icon-btn {
  width: 34px;
  height: 34px;
  padding: 0;
  position: relative;
  color: var(--accent-primary);
  border: none;
  background: transparent;
}

.header-btn.icon-btn:hover {
  color: var(--accent-primary);
  box-shadow: none;
  border: none;
}

.header-btn.icon-btn .btn-icon {
  width: 24px;
  height: 24px;
  margin: 0;
  transition: transform 0.15s ease;
}

.header-btn.icon-btn:hover .btn-icon {
  transform: scale(1.15);
}

/* CSS Tooltips */
[data-tooltip] {
  position: relative;
}

[data-tooltip]::after {
  content: attr(data-tooltip);
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  margin-top: 8px;
  padding: 6px 10px;
  background: var(--bg-elevated);
  border: 1px solid var(--border);
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  color: var(--text-primary);
  white-space: nowrap;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.15s, visibility 0.15s;
  pointer-events: none;
  z-index: 1000;
}

[data-tooltip]:hover::after {
  opacity: 1;
  visibility: visible;
}

/* Right-aligned tooltips for rightmost buttons */
.controls [data-tooltip]:last-child::after {
  left: auto;
  right: 0;
  transform: none;
}

/* Controls Section */
.controls {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  min-width: 180px;
  flex-shrink: 0;
}

/* Header Buttons - consistent height */
.header-btn {
  padding: 8px 16px;
  height: 34px;
  background: transparent;
  border: 1px solid var(--border);
  color: var(--text-secondary);
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: Inter, Arial, sans-serif;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-radius: 2px;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}

.header-btn:hover {
  border-color: var(--accent-primary);
  color: var(--accent-primary);
  box-shadow: var(--shadow-sm);
}

.controls button.active {
  background: var(--accent-primary);
  border-color: var(--accent-primary);
  color: var(--text-inverse);
}

.controls button.resume {
  border-color: var(--success);
  color: var(--success);
}

.controls button.resume:hover {
  background: var(--hover-overlay);
  box-shadow: var(--shadow-sm);
  border-color: var(--success);
}

/* Canvas Container - fills remaining space */
.pixi-container {
  flex: 1;
  width: 100%;
  position: relative;
  overflow: hidden;
  min-height: 0;
  /* Prevent page scrolling when dragging on canvas (iPad/mobile) */
  touch-action: none;
  /* Start invisible, fade in when loaded */
  opacity: 0;
  transition: opacity 0.4s ease-out;
}

.pixi-container.loaded {
  opacity: 1;
}

/* Recruit Button - Eye-catching solid style */
.recruit-btn {
  position: relative;
  display: flex;
  align-items: center;
  padding: 8px 20px;
  background: var(--accent-secondary);
  border: none;
  color: #fff;
  font-size: 12px;
  font-weight: 800;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: Inter, Arial, sans-serif;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-radius: 4px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(249, 115, 22, 0.4);
  animation: recruitGlow 2s ease-in-out infinite;
}

@keyframes recruitGlow {
  0%, 100% {
    box-shadow: 0 2px 12px rgba(249, 115, 22, 0.4);
  }
  50% {
    box-shadow: 0 2px 20px rgba(249, 115, 22, 0.6);
  }
}

.recruit-btn::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgba(255, 255, 255, 0.2) 50%,
    transparent 100%
  );
  animation: recruitShine 3s ease-in-out infinite;
}

@keyframes recruitShine {
  0% {
    left: -100%;
  }
  50%, 100% {
    left: 100%;
  }
}

.recruit-btn:hover {
  background: #ea7c2b;
  box-shadow: 0 4px 25px rgba(249, 115, 22, 0.6);
  transform: translateY(-1px);
  animation: none;
}

.recruit-btn:hover::before {
  animation: none;
}

.recruit-btn.enlisted {
  background: var(--hover-overlay);
  border-color: var(--success);
  color: var(--success);
  box-shadow: var(--shadow-sm);
  animation: enlistedPulse 2s ease-in-out infinite;
}

@keyframes enlistedPulse {
  0%, 100% {
    box-shadow: var(--shadow-sm);
  }
  50% {
    box-shadow: var(--shadow-md);
  }
}

.recruit-btn.enlisted::before {
  background: linear-gradient(
    90deg,
    transparent 0%,
    var(--hover-overlay) 50%,
    transparent 100%
  );
}

.recruit-btn.enlisted:hover {
  background: var(--active-overlay);
  box-shadow: var(--shadow-md);
  animation: none;
}

/* Status Indicator - small tooltip-style indicator */
.status-indicator {
  position: fixed;
  bottom: 12px;
  left: 12px;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border-radius: 4px;
  z-index: 1000;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  user-select: none;
  pointer-events: auto; /* Allow clicking retry button */
}

.status-indicator.syncing {
  background: #b45309;
}

.status-indicator.paused {
  background: #854d0e;
}

.status-indicator.connected {
  background: #166534;
}

.status-indicator.disconnected {
  background: #991b1b;
}

.status-indicator.reconnecting {
  background: #b45309;
}

.retry-btn {
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.4);
  color: white;
  padding: 2px 8px;
  border-radius: 3px;
  font-size: 11px;
  cursor: pointer;
  transition: background 0.2s;
  margin-left: 4px;
}

.retry-btn:hover {
  background: rgba(255, 255, 255, 0.3);
}

.status-spinner {
  width: 12px;
  height: 12px;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #fff;
}

.status-text {
  font-family: Inter, Arial, sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: #fff;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Minimized Tabs Bar */
.minimized-tabs-bar {
  position: fixed;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 4px;
  padding: 8px 12px;
  background: var(--bg-surface);
  border: 1px solid var(--border);
  border-bottom: none;
  border-radius: 8px 8px 0 0;
  box-shadow: var(--shadow-lg);
  z-index: 2000;
  max-width: calc(100vw - 200px);
  overflow-x: auto;
}

.minimized-tab {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  background: var(--hover-overlay);
  border: 1px solid var(--border);
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
}

.minimized-tab:hover {
  background: var(--active-overlay);
  border-color: var(--accent-primary);
}

.tab-title {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary);
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
}

.tab-close {
  background: transparent;
  border: none;
  padding: 2px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-muted);
  transition: color 0.2s;
  border-radius: 2px;
}

.tab-close:hover {
  color: var(--error);
  background: rgba(239, 68, 68, 0.1);
}

.tab-close-icon {
  width: 12px;
  height: 12px;
}

/* Mobile Menu Styles */
.mobile-menu-btn {
  height: 34px;
  width: 34px;
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
  color: var(--accent-primary);
  transition: all 0.15s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.mobile-menu-btn:hover {
  color: var(--accent-primary);
}

.menu-icon {
  width: 24px;
  height: 24px;
  transition: transform 0.15s ease;
}

.mobile-menu-btn:hover .menu-icon {
  transform: scale(1.15);
}

.mobile-menu-dropdown {
  position: absolute;
  top: 50px;
  left: 0;
  right: 0;
  background: var(--bg-surface);
  border-bottom: 1px solid var(--border);
  padding: 12px;
  z-index: 1500;
  display: flex;
  flex-direction: column;
  gap: 8px;
  box-shadow: var(--shadow-md);
}

.mobile-search-container {
  display: flex;
  align-items: center;
  background: var(--hover-overlay);
  border: 1px solid var(--border);
  border-radius: 4px;
  padding: 0 12px;
  height: 44px;
}

.mobile-search-container .search-icon {
  width: 18px;
  height: 18px;
  color: var(--text-muted);
  flex-shrink: 0;
}

.mobile-search-input {
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  padding: 0 12px;
  font-size: 14px;
  color: var(--text-primary);
}

.mobile-search-input::placeholder {
  color: var(--text-muted);
}

.mobile-menu-item {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  height: 44px;
  background: transparent;
  border: 1px solid var(--border);
  border-radius: 4px;
  color: var(--text-secondary);
  font-size: 14px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.mobile-menu-item:hover {
  border-color: var(--accent-primary);
  color: var(--accent-primary);
}

.mobile-menu-item.enlist {
  background: var(--accent-secondary);
  border-color: var(--accent-secondary);
  color: white;
}

.mobile-menu-item.enlist:hover {
  filter: brightness(1.1);
}

.mobile-menu-item.resume {
  border-color: var(--success);
  color: var(--success);
}

.mobile-menu-item .btn-icon {
  width: 16px;
  height: 16px;
}

/* Mobile mode adjustments - applied via v-show in JS */
.header-bar.mobile-mode {
  padding: 0 12px;
}

.header-bar.mobile-mode .logo-svg {
  height: 22px;
}
</style>
